import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogContent, Box, TextField, Button, FormControl, InputLabel, Select,
    MenuItem, Typography, InputAdornment, IconButton, Tabs, Tab, Snackbar, Alert
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Key, User, MapPin, Store, IdCard } from 'lucide-react';
import axios from 'axios';

interface User {
    id: number;
    username: string;
    role: string;
    cityId: string;
    myPharmacy?: string;
    userphone: string;
    fullName: string;
    iin: string;
}

interface ActionDialogData {
    password: string;
    role: string;
    myPharmacy: string;
    cityId: string;
    userphone: string;
    fullName: string;
    iin: string;
}

interface UserActionsDialogProps {
    open: boolean;
    onClose: () => void;
    user: User | null;
    actionDialog: ActionDialogData;
    setActionDialog: (data: ActionDialogData) => void;
    onSavePassword: () => void;
    onSaveRole: () => void;
    onSaveRegion: () => void;
    onSavePharmacy: () => void;
    setShouldRefetch: (value: boolean) => void;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

interface SnackbarState {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
}

interface Region {
    id: number;
    name: string;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
            style={{ padding: '20px 0' }}
        >
            {value === index && children}
        </div>
    );
};

const UserActionsDialog: React.FC<UserActionsDialogProps> = ({
    open,
    onClose,
    user,
    actionDialog,
    setActionDialog,
    onSavePassword,
    onSaveRole,
    onSaveRegion,
    onSavePharmacy,
    setShouldRefetch
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const currentUserRole = localStorage.getItem('role');
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'success'
    });

    const formatPhoneNumber = (value: string): string => {
        const digits = value.replace(/\D/g, '');

        if (digits.length === 0) return '+7';

        let formatted = '+7';
        if (digits.length > 1) {
            formatted += ' ' + digits.substr(1, 3);
        }
        if (digits.length > 4) {
            formatted += ' ' + digits.substr(4, 3);
        }
        if (digits.length > 7) {
            formatted += ' ' + digits.substr(7, 2);
        }
        if (digits.length > 9) {
            formatted += ' ' + digits.substr(9, 2);
        }

        return formatted;
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;

        if (!value.startsWith('+7')) {
            value = '+7' + value.replace('+', '');
        }

        const formattedPhone = formatPhoneNumber(value);

        if (formattedPhone.replace(/\D/g, '').length <= 11) {
            setActionDialog({ ...actionDialog, userphone: formattedPhone });
        }
    };

    const isValidPhone = (phone: string): boolean => {
        const digits = phone.replace(/\D/g, '');
        return digits.length === 11 && digits.startsWith('7');
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handlePharmacyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const formattedValue = value.replace(/\s+/g, ',');
        setActionDialog({ ...actionDialog, myPharmacy: formattedValue });
    };

    const handleUserDataSave = async () => {
        if (!user) return;

        try {
            const response = await axios.post('https://tbl.zerdegroup.kz/api/user-data-update', {
                username: user.username,
                userphone: actionDialog.userphone,
                fullName: actionDialog.fullName,
                iin: actionDialog.iin
            });

            if (response.data.success) {
                showNotification('Данные пользователя успешно сохранены', 'success');
                setShouldRefetch(true);
                onClose();
            } else {
                showNotification(`Ошибка сохранения данных: ${response.data.error}`, 'error');
            }
        } catch (error) {
            console.error('Ошибка при обновлении данных пользователя:', error);
            showNotification('Не удалось обновить данные. Проверьте соединение с сервером.', 'error');
        }
    };

    const [regions, setRegions] = useState<Region[]>([]);

    const fetchRegions = async () => {
        try {
            const response = await axios.get('https://tbl.zerdegroup.kz/api/regions');
            if (response.data.success) {
                setRegions(response.data.data);
            } else {
                showNotification('Ошибка при загрузке регионов', 'error');
            }
        } catch (error) {
            console.error('Ошибка при загрузке регионов:', error);
            showNotification('Ошибка при загрузке регионов', 'error');
        }
    };

    useEffect(() => {
        fetchRegions();
    }, []);

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    }
                }}
            >
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'primary.main', color: 'white', p: 2 }}>
                    <Typography variant="h6" component="div">
                        Данные пользователя {user?.username}
                    </Typography>
                    <Tabs
                        value={activeTab}
                        onChange={(_, newValue) => setActiveTab(newValue)}
                        textColor="inherit"
                        TabIndicatorProps={{
                            style: { backgroundColor: 'white' }
                        }}
                    >
                        <Tab icon={<Key size={20} />} label="Пароль" />
                        <Tab icon={<User size={20} />} label="Роль" />
                        <Tab icon={<IdCard size={20} />} label="Данные" />
                        {/* {actionDialog.role === 'user' && <Tab icon={<MapPin size={20} />} label="Регион" />} */}
                        <Tab icon={<MapPin size={20} />} label="Регион" />
                        {actionDialog.role === 'view' || actionDialog.role === 'km' || actionDialog.role === 'to' ? <Tab icon={<Store size={20} />} label="Аптеки" /> : null}
                    </Tabs>
                </Box>

                <DialogContent>
                    <TabPanel value={activeTab} index={0}>
                        <TextField
                            label="Новый пароль"
                            type={showPassword ? 'text' : 'password'}
                            value={actionDialog.password}
                            onChange={(e) => setActionDialog({ ...actionDialog, password: e.target.value })}
                            fullWidth
                            sx={{ mb: 2 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={onSavePassword}
                            disabled={!actionDialog.password}
                            fullWidth
                        >
                            Сохранить пароль
                        </Button>
                    </TabPanel>

                    <TabPanel value={activeTab} index={1}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Роль пользователя</InputLabel>
                            <Select
                                value={actionDialog.role}
                                onChange={(e) => setActionDialog({ ...actionDialog, role: e.target.value as string })}
                                label="Роль пользователя"
                                disabled={currentUserRole !== 'admin'}
                            >
                                <MenuItem value="user">Аптека</MenuItem>
                                <MenuItem value="storage">Склад</MenuItem>
                                <MenuItem value="guide">Справочная</MenuItem>
                                <MenuItem value="view">Просмотр</MenuItem>
                                <MenuItem value="km">Отдел КМ</MenuItem>
                                <MenuItem value="to">Торг. отдел</MenuItem>
                                <MenuItem value="moder">Модератор</MenuItem>
                                <MenuItem value="full-report">Доступ к отчетам</MenuItem>
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            onClick={onSaveRole}
                            disabled={!actionDialog.role || actionDialog.role === user?.role}
                            fullWidth
                        >
                            Сохранить роль
                        </Button>
                    </TabPanel>

                    <TabPanel value={activeTab} index={2}>
                        <TextField
                            label="ФИО"
                            value={actionDialog.fullName}
                            onChange={(e) => setActionDialog({ ...actionDialog, fullName: e.target.value })}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Телефон"
                            value={actionDialog.userphone || '+7'}
                            onChange={handlePhoneChange}
                            fullWidth
                            sx={{ mb: 2 }}
                            inputProps={{
                                maxLength: 16, // +7 XXX XXX XX XX
                            }}
                            placeholder="+7 700 000 00 00"
                            error={actionDialog.userphone.trim() !== '' && !isValidPhone(actionDialog.userphone)}
                            helperText={actionDialog.userphone.trim() !== '' && !isValidPhone(actionDialog.userphone) ?
                                "Введите корректный номер телефона" : ""}
                        />
                        <TextField
                            label="ИИН"
                            value={actionDialog.iin}
                            onChange={(e) => setActionDialog({ ...actionDialog, iin: e.target.value })}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleUserDataSave}
                            disabled={
                                !actionDialog.fullName ||
                                !actionDialog.userphone ||
                                !actionDialog.iin ||
                                (actionDialog.fullName === user?.fullName && actionDialog.userphone === user?.userphone && actionDialog.iin === user?.iin)
                            }
                            fullWidth
                        >
                            Сохранить данные
                        </Button>
                    </TabPanel>

                    {/* {actionDialog.role === 'user' || actionDialog.role === 'moder' || actionDialog.role === 'view' && ( */}
                    <TabPanel value={activeTab} index={3}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Регион</InputLabel>
                            <Select
                                value={actionDialog.cityId}
                                onChange={(e) => setActionDialog({ ...actionDialog, cityId: e.target.value as string })}
                                label="Регион"
                                disabled={currentUserRole === 'view' || currentUserRole === 'km' || currentUserRole === 'to'}
                            >
                                {regions.map((region) => (
                                    <MenuItem key={region.id} value={region.name}>
                                        {region.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            onClick={onSaveRegion}
                            disabled={!actionDialog.cityId || actionDialog.cityId === user?.cityId}
                            fullWidth
                        >
                            Сохранить регион
                        </Button>
                    </TabPanel>
                    {/* )} */}

                    {(actionDialog.role === 'view' || actionDialog.role === 'km' || actionDialog.role === 'to') && (
                        <TabPanel value={activeTab} index={4}>
                            <TextField
                                label="Подчиненные аптеки"
                                value={actionDialog.myPharmacy}
                                onChange={handlePharmacyChange}
                                fullWidth
                                multiline
                                rows={3}
                                sx={{ mb: 2 }}
                                placeholder="Введите номера аптек через запятую"
                            />
                            <Button
                                variant="contained"
                                onClick={onSavePharmacy}
                                disabled={actionDialog.myPharmacy === user?.myPharmacy}
                                fullWidth
                            >
                                Сохранить список аптек
                            </Button>
                        </TabPanel>
                    )}
                </DialogContent>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default UserActionsDialog;